import { alpha, Components, darken, getContrastRatio, lighten, Theme } from '@mui/material/styles';

import { ColorPalette } from './theme';

export const DEFAULT_FONT = 'Montserrat';

// Palette colors the `soft` button variant is generated for. `inherit` is
// excluded — it has no `.main` channel and is handled as its own variant below.
const SOFT_BUTTON_COLORS = ['primary', 'secondary', 'info', 'success', 'warning', 'error'] as const;

// Shape of a single entry in a component's `variants` array (used by both
// `MuiButton.variants` below and `MuiToggleButtonGroup.variants` further
// down). Declared locally so this file does not depend on the `soft` module
// augmentation being loaded — see the note on the `MuiButton.variants` array.
type SoftButtonVariant = {
  props: Record<string, unknown>;
  style: (arg: { theme: Theme }) => Record<string, unknown>;
};

// The palette colors a `ToggleButtonGroup` track can be tinted. `primary` is
// included even though it duplicates the default root style below, so
// `color="primary"` is an explicit, intentional choice rather than relying
// on what happens to fall out of not setting `color` at all.
const TOGGLE_GROUP_COLORS = ['primary', 'success', 'warning', 'info'] as const;

// Palette colors the `soft` chip variant is generated for. `default` is
// excluded — it has no palette channel and is handled as its own variant below.
const SOFT_CHIP_COLORS = ['primary', 'secondary', 'info', 'success', 'warning', 'error'] as const;

// Label color for a soft chip on its tinted background. Uses the palette's
// `.dark` when it is readable (WCAG AA, 4.5:1), else darkens `.main` only as
// far as needed — some palettes (e.g. the FIT `secondary`) have a `.dark`
// lighter than `.main`, and FIT `warning.dark` is too light on its own tint.
const SOFT_CHIP_MIN_CONTRAST = 4.5;
const softChipLabelColor = (main: string, dark: string, background: string): string => {
  if (getContrastRatio(dark, background) >= SOFT_CHIP_MIN_CONTRAST) {
    return dark;
  }
  for (let amount = 0.1; amount < 1; amount += 0.1) {
    const candidate = darken(main, amount);
    if (getContrastRatio(candidate, background) >= SOFT_CHIP_MIN_CONTRAST) {
      return candidate;
    }
  }
  return darken(main, 0.8);
};

export const componentsOverride = (palette: ColorPalette, font?: string): Components<Omit<Theme, 'components'>> => {
  const fontFamily = `${font || DEFAULT_FONT}, Arial, sans-serif`;

  return {
    MuiCssBaseline: {
      styleOverrides: {
        '*, *::before, *::after': {
          fontFamily,
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: lighten(palette.primary.main, 0.92),
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          backgroundColor: '#fafafa',
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
        },
        // MUI offsets the action slot with `padding: 4px 0 0 16px` and pins it to
        // `flex-start`, which leaves the button sitting low against the message.
        // Drop the top padding and center it instead.
        action: {
          alignItems: 'center',
          paddingTop: 0,
        },
        // The `standard` variant paints a pale tint of the palette color, so its
        // text must be the *dark* shade — not `contrastText`, which is meant for
        // text sitting on the solid fill (it is `#fff` in the fit palette, i.e.
        // white on pale blue).
        standardInfo: {
          color: palette.info.dark,
          '& .MuiAlert-icon': {
            color: palette.info.dark,
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontSize: '1rem',
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          marginBottom: '4px',
          borderRadius: '4px',
          '&.Mui-selected': {
            backgroundColor: lighten(palette.primary.main, 0.8),
            borderColor: palette.primary.main,
          },
          '&:hover': {
            borderColor: lighten(palette.primary.main, 0.3),
            backgroundColor: lighten(palette.primary.main, 0.7),
          },
        },
      },
    },
    MuiFormGroup: {
      styleOverrides: {
        root: {
          border: '1px solid #E0E0E0',
          borderRadius: '8px',
          padding: '15px',
        },
      },
    },
    MuiButton: {
      defaultProps: {
        variant: 'contained' as const,
        color: 'primary' as const,
      },
      // `soft` — tinted background, no border, colored label. Sits between `text`
      // and `contained` in emphasis. Declared per-color because MUI v5 matches
      // variants on exact prop values, not on `color` generically.
      //
      // Typed as `SoftButtonVariant[]` rather than relying on the `soft` module
      // augmentation: consuming apps compile this file inside their own program,
      // where the sibling `mui-augmentation.d.ts` is not included, so `variant:
      // 'soft'` would not typecheck there. See that file for the full story.
      variants: [
        ...SOFT_BUTTON_COLORS.map(
          (color): SoftButtonVariant => ({
            props: { variant: 'soft', color },
            style: ({ theme }: { theme: Theme }) => ({
              border: 0,
              color: theme.palette[color].main,
              backgroundColor: alpha(theme.palette[color].main, 0.12),
              '&:hover': {
                backgroundColor: alpha(theme.palette[color].main, 0.22),
              },
              '&.Mui-disabled': {
                border: 0,
                color: theme.palette.grey[400],
                background: theme.palette.action.disabledBackground,
              },
            }),
          }),
        ),
        {
          props: { variant: 'soft', color: 'inherit' },
          style: ({ theme }: { theme: Theme }) => ({
            border: 0,
            color: theme.palette.grey[800],
            backgroundColor: theme.palette.grey[200],
            '&:hover': {
              backgroundColor: theme.palette.grey[300],
            },
            '&.Mui-disabled': {
              border: 0,
              color: theme.palette.grey[400],
              background: theme.palette.action.disabledBackground,
            },
          }),
        },
      ] as SoftButtonVariant[] as NonNullable<Components<Omit<Theme, 'components'>>['MuiButton']>['variants'],
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.primary.contrastText,
          borderRadius: '12px',
          px: 3,
          paddingTop: '2px',
          paddingBottom: '2px',
          textTransform: 'none',
          justifyContent: 'space-between',
          minWidth: 'auto',
          minHeight: '40px',
          '&.MuiButton-containedPrimary': {
            background: `linear-gradient(95.35deg, ${theme.palette.primary.main} 11.81%, ${theme.palette.primary.light} 84.7%)`,
          },
          '&.MuiButton-containedInfo': {
            background: `linear-gradient(95.35deg, ${theme.palette.info.main} 11.81%, ${theme.palette.info.light} 84.7%)`,
          },
          '&.MuiButton-outlinedPrimary': {
            color: `${theme.palette.primary.main}`,
          },
          '&.MuiButton-outlinedSecondary': {
            color: `${theme.palette.secondary.main}`,
          },
          '&.MuiButton-outlinedSuccess': {
            color: `${theme.palette.success.main}`,
          },
          '&.MuiButton-outlinedWarning': {
            color: `${theme.palette.warning.main}`,
          },
          '&.MuiButton-outlinedInfo': {
            color: `${theme.palette.info.main}`,
          },
          '&.MuiButton-outlinedError': {
            color: `${theme.palette.primary.main}`,
          },
          '&.MuiButton-outlinedInherit': {
            color: theme.palette.grey[600],
            borderColor: theme.palette.grey[300],
            '&:hover': {
              borderColor: theme.palette.grey[400],
              backgroundColor: theme.palette.grey[100],
            },
          },
          '&.MuiButton-text': {
            paddingLeft: '16px',
            paddingRight: '16px',
          },
          '&.MuiButton-textPrimary': {
            color: theme.palette.primary.main,
            '&:hover': { backgroundColor: `${theme.palette.primary.main}14` },
          },
          '&.MuiButton-textSecondary': {
            color: theme.palette.secondary.main,
            '&:hover': { backgroundColor: `${theme.palette.secondary.main}14` },
          },
          '&.MuiButton-textError': {
            color: theme.palette.error.main,
            '&:hover': { backgroundColor: `${theme.palette.error.main}14` },
          },
          '&.MuiButton-textSuccess': {
            color: theme.palette.success.main,
            '&:hover': { backgroundColor: `${theme.palette.success.main}14` },
          },
          '&.MuiButton-textInherit': {
            color: theme.palette.grey[800],
            '&:hover': { backgroundColor: theme.palette.grey[100] },
          },
          // `background` shorthand, not `backgroundColor` — the contained variants above
          // set a gradient via the shorthand, which MUI's own disabled styling cannot reset.
          // Must stay after those rules: equal specificity, so source order decides.
          '&.Mui-disabled': {
            border: 0,
            color: theme.palette.grey[400],
            background: theme.palette.action.disabledBackground,
          },
          '&.MuiButton-sizeLarge': {
            height: '50px',
          },
        }),
        endIcon: {
          marginRight: '-14px',
        },
      },
    },
    MuiChip: {
      // `soft` — tinted background, no border, dark label. Same tint as the
      // colored chip overrides below. Declared per-color for the same reason as
      // `MuiButton.variants`. The clickable hover replaces MUI's default, which
      // switches a colored chip to its solid `.dark` shade. The `.MuiChip-color*`
      // overrides below skip soft chips (`:not(.MuiChip-soft)`) so their
      // higher specificity does not replace the readable soft label color.
      variants: [
        ...SOFT_CHIP_COLORS.map(
          (color): SoftButtonVariant => ({
            props: { variant: 'soft', color },
            style: ({ theme }: { theme: Theme }) => {
              const { main, dark } = theme.palette[color];
              const backgroundColor = lighten(main, 0.88);
              const label = softChipLabelColor(main, dark, backgroundColor);
              return {
                border: 0,
                color: label,
                backgroundColor,
                '& .MuiChip-icon, & .MuiChip-deleteIcon': {
                  color: alpha(label, 0.8),
                },
                '& .MuiChip-deleteIcon:hover': {
                  color: label,
                },
                '&.MuiChip-clickable:hover, &.MuiChip-clickable.Mui-focusVisible': {
                  backgroundColor: lighten(main, 0.76),
                },
              };
            },
          }),
        ),
        {
          props: { variant: 'soft', color: 'default' },
          style: ({ theme }: { theme: Theme }) => ({
            border: 0,
            color: theme.palette.grey[800],
            backgroundColor: theme.palette.grey[200],
            '& .MuiChip-icon, & .MuiChip-deleteIcon': {
              color: theme.palette.grey[600],
            },
            '& .MuiChip-deleteIcon:hover': {
              color: theme.palette.grey[800],
            },
            '&.MuiChip-clickable:hover, &.MuiChip-clickable.Mui-focusVisible': {
              backgroundColor: theme.palette.grey[300],
            },
          }),
        },
      ] as SoftButtonVariant[] as NonNullable<Components<Omit<Theme, 'components'>>['MuiChip']>['variants'],
      styleOverrides: {
        root: {
          '&.MuiChip-colorSuccess:not(.MuiChip-soft)': {
            backgroundColor: lighten(palette.success.main, 0.88),
            color: palette.success.dark,
          },
          '&.MuiChip-colorError:not(.MuiChip-soft)': {
            backgroundColor: lighten(palette.error.main, 0.88),
            color: palette.error.dark,
          },
          '&.MuiChip-colorWarning:not(.MuiChip-soft)': {
            backgroundColor: lighten(palette.warning.main, 0.88),
            color: palette.warning.dark,
          },
          '&.MuiChip-colorInfo:not(.MuiChip-soft)': {
            backgroundColor: lighten(palette.info.light, 0.7),
            color: palette.info.dark,
          },
          '&.MuiChip-colorPrimary:not(.MuiChip-soft)': {
            backgroundColor: lighten(palette.primary.main, 0.88),
            color: palette.primary.dark,
          },
          '&.MuiChip-outlinedSuccess': {
            color: palette.success.main,
          },
          '&.MuiChip-outlinedError': {
            color: palette.error.main,
          },
          '&.MuiChip-outlinedWarning': {
            color: palette.warning.main,
          },
          '&.MuiChip-outlinedInfo': {
            color: palette.info.main,
          },
          '&.MuiChip-outlinedPrimary': {
            color: palette.primary.main,
          },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          padding: '12px 16px',
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          paddingTop: '2px',
          width: '100%',
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          '&.MuiOutlinedInput-root': {
            borderRadius: '20px',
          },
          '&.MuiOutlinedInput-root.MuiInputBase-adornedEnd': {
            paddingRight: '9px',
          },
        },
      },
    },
    // `filled` TextField variant, restyled as a soft "this is an input" affordance:
    // tinted grey resting background, no underline, rounded corners. Focus swaps to
    // a light-primary background with a primary ring so the active field reads clearly.
    MuiFilledInput: {
      defaultProps: {
        disableUnderline: true,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: 8,
          backgroundColor: theme.palette.action.hover,
          border: '1px solid transparent',
          transition: theme.transitions.create(['background-color', 'border-color']),
          '&:hover': {
            backgroundColor: theme.palette.action.selected,
          },
          '&.Mui-focused': {
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
            borderColor: alpha(theme.palette.primary.main, 0.5),
          },
          '&.Mui-error.Mui-focused': {
            backgroundColor: alpha(theme.palette.error.main, 0.08),
            borderColor: alpha(theme.palette.error.main, 0.5),
          },
          '&.Mui-disabled': {
            backgroundColor: theme.palette.action.disabledBackground,
          },
        }),
        // No underline and (usually) no floating label — reclaim MUI's reserved
        // top padding so text sits centered.
        input: {
          padding: '10px 12px',
          '&.MuiInputBase-inputSizeSmall': {
            padding: '8px 12px',
          },
        },
        // Multiline: the <textarea> gets the padding, the root wrapper stays flush.
        multiline: {
          padding: 0,
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        inputRoot: {
          '&.MuiOutlinedInput-root': {
            padding: '9px 22px !important',
          },
        },
        root: {
          marginTop: '10px',
          borderRadius: 20,
          '& .MuiFormLabel-root': {
            padding: '6px 9px',
          },
          '& .MuiInputLabel-root': {
            top: '-3px',
            left: '-7px',
          },
          '& .MuiTextField-root': {
            paddingTop: `0 !important`,
          },
        },
        // Rounded, softly-shadowed dropdown surface.
        paper: {
          borderRadius: '20px',
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
        },
        // Grouped option headers: light primary background with primary.dark text.
        groupLabel: ({ theme }) => ({
          fontWeight: 700,
          fontSize: '1rem',
          color: theme.palette.primary.dark,
          backgroundColor: lighten(theme.palette.primary.main, 0.9),
        }),
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          top: '4px',
          '&:not(.MuiInputLabel-shrink)': {
            left: '12px',
          },
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          backgroundColor: palette.primary.main,
          color: palette.common.white,
          marginBottom: '12px',
          fontSize: '1.2rem',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 24,
        },
        paperFullScreen: {
          borderRadius: 0,
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '16px 24px 16px',
        },
      },
    },
    MuiSlider: {
      styleOverrides: {
        root: {
          '& .MuiSlider-thumb': {
            background: '#fff',
            boxShadow: '0px 5px 8px 0px rgba(0, 0, 0, 0.25)',
          },
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        h2: ({ theme }) => ({
          fontSize: '36px',
          fontWeight: '700',
          color: palette.grey[900],
          [theme.breakpoints.down('md')]: {
            fontSize: '30px',
          },
          [theme.breakpoints.down(376)]: {
            fontSize: '22px',
          },
        }),
        h3: ({ theme }) => ({
          color: palette.grey[800],
          [theme.breakpoints.down('md')]: {
            fontSize: '28px',
          },
          [theme.breakpoints.down(376)]: {
            fontSize: '20px',
          },
        }),
        h4: ({ theme }) => ({
          color: palette.grey[800],
          [theme.breakpoints.down('md')]: {
            fontSize: '26px',
          },
          [theme.breakpoints.down(376)]: {
            fontSize: '18px',
          },
        }),
        h5: {
          color: palette.grey[800],
        },
        h6: ({ theme }) => ({
          fontSize: '20px',
          color: palette.grey[800],
          [theme.breakpoints.down('md')]: {
            fontSize: '18px',
          },
          [theme.breakpoints.down(376)]: {
            fontSize: '16px',
          },
        }),
        body1: ({ theme }) => ({
          fontSize: '18px',
          fontWeight: '600',
          [theme.breakpoints.down('md')]: {
            fontSize: '16px',
          },
          [theme.breakpoints.down(376)]: {
            fontSize: '14px',
          },
        }),
        body2: ({ theme }) => ({
          fontSize: '15px',
          fontWeight: '400',
          [theme.breakpoints.down('md')]: {
            fontSize: '14px',
          },
          [theme.breakpoints.down(376)]: {
            fontSize: '12px',
          },
        }),
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          border: '1px solid #dcdcdc',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: '10px',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: `4px 4px 0 0`,
          textTransform: 'none',
          '&.Mui-selected': {
            backgroundColor: lighten(theme.palette.primary.main, 0.9),
          },
        }),
      },
    },
    // A segmented control, not a row of outlined buttons: a tinted track with
    // the selected segment raised on a white card + shadow. The track tints
    // to the group's `color` prop (primary/success/warning/info) via the
    // `variants` below, so a call site picks the track's color the same way
    // it already picks `MuiButton`'s `soft` variant color - no more call site
    // reaching for a one-off `sx` override to get a track that isn't pink.
    // `color` unset (or any color besides the four below) still falls back to
    // the pink default root style, same as it always did. `flex: 1` only
    // matters when the group has `fullWidth` (it otherwise sits on content
    // width already, so this is a no-op there).
    MuiToggleButtonGroup: {
      styleOverrides: {
        root: {
          backgroundColor: lighten(palette.primary.light, 0.95),
          border: `1px solid ${lighten(palette.primary.light, 0.9)}`,
          borderRadius: 8,
          padding: 4,
          gap: 4,
        },
      },
      variants: TOGGLE_GROUP_COLORS.map(
        (color): SoftButtonVariant => ({
          props: { color },
          style: ({ theme }: { theme: Theme }) => {
            // `primary`'s pastel tint lives on `.light` already in this palette,
            // not `.main` - mirror the default root exactly rather than
            // deriving a second, slightly different pink from `.main`.
            const base = color === 'primary' ? theme.palette.primary.light : theme.palette[color].main;
            return {
              backgroundColor: lighten(base, color === 'primary' ? 0.95 : 0.9),
              border: `1px solid ${lighten(base, color === 'primary' ? 0.9 : 0.82)}`,
            };
          },
        }),
      ) as SoftButtonVariant[] as NonNullable<
        Components<Omit<Theme, 'components'>>['MuiToggleButtonGroup']
      >['variants'],
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          flex: 1,
          border: 0,
          borderRadius: '6px !important',
          textTransform: 'none',
          color: palette.grey[700],
          padding: '4px 10px',
          whiteSpace: 'nowrap',
          '&.Mui-selected': {
            backgroundColor: '#fff',
            color: palette.success.main,
            fontWeight: 600,
            boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.12)',
            '&:hover': {
              backgroundColor: '#fff',
            },
          },
          // The selected segment's label picks up the group's `color` prop
          // (primary/success/warning/info/...) instead of a flat dark grey,
          // so a redirect-type toggle and a status toggle can read
          // differently without a different track style.
          '&.MuiToggleButton-colorPrimary.Mui-selected': {
            color: palette.primary.main,
          },
          '&.MuiToggleButton-colorSecondary.Mui-selected': {
            color: palette.secondary.main,
          },
          '&.MuiToggleButton-colorSuccess.Mui-selected': {
            color: palette.success.main,
          },
          '&.MuiToggleButton-colorWarning.Mui-selected': {
            color: palette.warning.main,
          },
          '&.MuiToggleButton-colorInfo.Mui-selected': {
            color: palette.info.main,
          },
          '&.MuiToggleButton-colorError.Mui-selected': {
            color: palette.error.main,
          },
          '&.Mui-disabled': {
            border: 0,
            color: palette.grey[400],
          },
        },
      },
    },
  };
};
